// Payment-provider status console (admin-portal): the COLLECTIONS plane
// (tenant subscription payments via billing-service) and the DISBURSEMENTS
// plane (payroll payouts via payroll-service), side by side. Read-only —
// the APIs return configuration booleans only, never secret values.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Row, Space, Statistic, Table, Tag, Typography,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, CreditCardOutlined,
  ExclamationCircleOutlined, LockOutlined, ReloadOutlined, WarningOutlined,
} from '@ant-design/icons';
import * as api from '../../services/paymentsService';
import { Link } from 'react-router-dom';

const { Title, Text } = Typography;

const Check: React.FC<{ ok: boolean }> = ({ ok }) =>
  ok ? (
    <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
  ) : (
    <WarningOutlined style={{ color: '#bfbfbf' }} />
  );

const configuredTag = (configured: boolean) =>
  configured ? <Tag color="green">Configured</Tag> : <Tag>Not configured</Tag>;

// ---------------------------------------------------------------------------
// Collections card
// ---------------------------------------------------------------------------
const CollectionsCard: React.FC<{ data: api.CollectionsStatus | null; loading: boolean; error: boolean }> = ({
  data, loading, error,
}) => (
  <Card title="Collections — subscription payments" loading={loading}>
    {error ? (
      <Alert type="error" showIcon message="Unavailable" description="Could not load collections status from billing-service." />
    ) : data ? (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Table
          size="small"
          pagination={false}
          rowKey="name"
          dataSource={[
            {
              name: 'M-Pesa (direct)',
              status: data.providers.mpesa
                ? data.providers.mpesa.available
                  ? <Tag color="green">Collecting</Tag>
                  : data.providers.mpesa.configured
                    ? <Tag color="orange">{data.providers.mpesa.enabled ? 'Not available' : 'Disabled'}</Tag>
                    : <Tag>Not configured</Tag>
                : <Tag color="orange">Status unavailable</Tag>,
              detail: data.providers.mpesa ? (
                <Space>
                  {data.providers.mpesa.environment && <Tag color={data.providers.mpesa.environment === 'live' ? 'red' : 'blue'}>{data.providers.mpesa.environment}</Tag>}
                  {data.providers.mpesa.credential_version != null && <span>v{data.providers.mpesa.credential_version}</span>}
                  <Link to="/billing/collection">Manage</Link>
                </Space>
              ) : null,
            },
            {
              name: 'AzamPay',
              status: configuredTag(data.providers.azampay.configured),
              detail: (
                <Space>
                  <Tag color="blue">{data.providers.azampay.environment}</Tag>
                  <span>
                    Callback token{' '}
                    {data.providers.azampay.callback_token_set ? (
                      <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: 'var(--color-danger)' }} />
                    )}
                  </span>
                </Space>
              ),
            },
            { name: 'Stripe', status: configuredTag(data.providers.stripe.configured), detail: null },
            { name: 'Manual', status: configuredTag(data.providers.manual.configured), detail: <Text type="secondary">Always available</Text> },
          ]}
          columns={[
            { title: 'Provider', dataIndex: 'name' },
            { title: 'Status', dataIndex: 'status' },
            { title: 'Details', dataIndex: 'detail' },
          ]}
        />

        {(data.unknown_payments ?? 0) > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${data.unknown_payments} payment(s) being confirmed with the provider`}
            description="Outcome unknown — never retried. The status worker resolves them; customers are told not to pay again."
          />
        )}

        {data.pending_payments > 0 && (
          <Statistic
            title="Pending payments"
            value={data.pending_payments}
            valueStyle={{ color: 'var(--color-warning)' }}
            prefix={<ExclamationCircleOutlined />}
          />
        )}

        <Card size="small" type="inner" title="Payments (30d)">
          {data.payments_last_30d.length ? (
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => `${r.provider}-${r.status}`}
              dataSource={data.payments_last_30d}
              columns={[
                { title: 'Provider', dataIndex: 'provider' },
                { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag>{s}</Tag> },
                { title: 'Count', dataIndex: 'count' },
              ]}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payments in the last 30 days" />
          )}
        </Card>

        <Card size="small" type="inner" title="Webhook deliveries (7d)">
          {data.webhooks_last_7d.length ? (
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => `${r.provider}-${r.outcome}`}
              dataSource={data.webhooks_last_7d}
              columns={[
                { title: 'Provider', dataIndex: 'provider' },
                {
                  title: 'Outcome',
                  dataIndex: 'outcome',
                  render: (o: string) => (
                    <Tag color={o === 'error' ? 'red' : o === 'duplicate' ? 'default' : 'green'}>{o}</Tag>
                  ),
                },
                { title: 'Count', dataIndex: 'count' },
              ]}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No webhook deliveries in the last 7 days" />
          )}
        </Card>
      </Space>
    ) : null}
  </Card>
);

// ---------------------------------------------------------------------------
// Disbursements card
// ---------------------------------------------------------------------------
const connectorTag = (connector: string) => {
  if (connector === 'mock') return <Tag>Simulated</Tag>;
  if (connector === 'azampay' || connector === 'aggregator') return <Tag color="blue">{connector}</Tag>;
  return <Tag color="blue">{connector}</Tag>;
};

const configLabels: { key: keyof api.DisbursementStatus['config']; label: string; checksum?: boolean }[] = [
  { key: 'checksum_key', label: 'Checksum key', checksum: true },
  { key: 'checksum_order', label: 'Checksum field order', checksum: true },
  { key: 'webhook_secret', label: 'Webhook secret' },
  { key: 'webhook_token', label: 'Webhook URL token' },
  { key: 'ceiling', label: 'Release ceiling' },
];

const DisbursementsCard: React.FC<{
  data: api.DisbursementStatus | null; loading: boolean; error: boolean; forbidden: boolean;
}> = ({ data, loading, error, forbidden }) => (
  <Card title="Disbursements — payroll payouts" loading={loading}>
    {forbidden ? (
      <Empty
        image={<LockOutlined style={{ fontSize: 40, color: '#bfbfbf' }} />}
        description={
          <Space direction="vertical" size={0}>
            <Text strong>Platform operators only</Text>
            <Text type="secondary">Disbursement status is visible to platform owners and admins.</Text>
          </Space>
        }
      />
    ) : error ? (
      <Alert type="error" showIcon message="Unavailable" description="Could not load disbursement status from payroll-service." />
    ) : data ? (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap>
          <Text strong>Connector:</Text>
          {connectorTag(data.connector)}
          {data.capabilities.bulk && <Tag color="geekblue">bulk</Tag>}
          {data.capabilities.verify && <Tag color="geekblue">verify</Tag>}
          {data.capabilities.channels.map((c) => (
            <Tag key={c} color="cyan">{c}</Tag>
          ))}
        </Space>

        <Card size="small" type="inner" title="Configuration checklist">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {configLabels.map(({ key, label, checksum }) => (
              <div key={key}>
                <Space>
                  <Check ok={data.config[key]} />
                  <Text>{label}</Text>
                </Space>
                {checksum && !data.config[key] && (
                  <div style={{ marginLeft: 22 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Awaiting AzamPay-provided key/field order
                    </Text>
                  </div>
                )}
              </div>
            ))}
          </Space>
        </Card>

        <Card size="small" type="inner" title="Items (30d)">
          {Object.keys(data.last_30d_counts).length ? (
            <Table
              size="small"
              pagination={false}
              rowKey="status"
              dataSource={Object.entries(data.last_30d_counts).map(([status, count]) => ({ status, count }))}
              columns={[
                { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag>{s}</Tag> },
                { title: 'Count', dataIndex: 'count' },
              ]}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No disbursement items in the last 30 days" />
          )}
        </Card>

        {data.reconciliation_backlog > 0 && (
          <div>
            <Statistic
              title="Reconciliation backlog"
              value={data.reconciliation_backlog}
              valueStyle={{ color: 'var(--color-danger)' }}
              prefix={<ExclamationCircleOutlined />}
            />
            {data.oldest_parked_submission && (
              <Text type="secondary">
                Oldest parked submission: {fmtDate(data.oldest_parked_submission)}
              </Text>
            )}
          </div>
        )}
      </Space>
    ) : null}
  </Card>
);

// ---------------------------------------------------------------------------
// Page — the two fetches are independent: one failing renders its own card's
// error state without breaking the other.
// ---------------------------------------------------------------------------
// Cross-tenant failed disbursement batches — the drilldown behind the dashboard's
// "failed disbursements" attention item (payroll platform endpoint).
const FailedBatchesCard: React.FC = () => {
  const [rows, setRows] = useState<api.PlatformBatch[] | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error' | 'forbidden'>('loading');
  const load = useCallback(() => {
    setState('loading');
    api.getDisbursementBatches('failed')
      .then((b) => { setRows(b); setState('ok'); })
      .catch((e: any) => setState(e?.response?.status === 403 ? 'forbidden' : 'error'));
  }, []);
  useEffect(load, [load]);

  if (state === 'forbidden') return null;
  return (
    <Card
      title="Failed disbursement batches — all tenants"
      loading={state === 'loading'}
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={load} />}
    >
      {state === 'error' ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Could not load failed batches." />
      ) : !rows || rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No failed batches." />
      ) : (
        <Table
          size="small"
          rowKey="id"
          dataSource={rows}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          columns={[
            { title: 'Tenant', dataIndex: 'tenant_id', render: (t: string) => <Text code>{t}</Text> },
            { title: 'Batch', dataIndex: 'id', render: (id: string) => <Text code>{id.slice(0, 8)}</Text> },
            { title: 'Connector', dataIndex: 'connector' },
            { title: 'Items', dataIndex: 'item_count' },
            { title: 'Amount', key: 'amt', render: (_: unknown, b: api.PlatformBatch) => `${b.total_amount} ${b.currency}` },
            { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag color="error">{s}</Tag> },
            { title: 'Prepared', dataIndex: 'prepared_at', render: (d: string | null) => (d ? fmtDateTime(d) : '—') },
          ]}
        />
      )}
    </Card>
  );
};

export const PaymentsPage: React.FC = () => {
  const [collections, setCollections] = useState<api.CollectionsStatus | null>(null);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [collectionsError, setCollectionsError] = useState(false);

  const [disb, setDisb] = useState<api.DisbursementStatus | null>(null);
  const [disbLoading, setDisbLoading] = useState(true);
  const [disbError, setDisbError] = useState(false);
  const [disbForbidden, setDisbForbidden] = useState(false);

  const load = useCallback(() => {
    setCollectionsLoading(true);
    setCollectionsError(false);
    api.getCollectionsStatus()
      .then(setCollections)
      .catch(() => { setCollections(null); setCollectionsError(true); })
      .finally(() => setCollectionsLoading(false));

    setDisbLoading(true);
    setDisbError(false);
    setDisbForbidden(false);
    api.getDisbursementStatus()
      .then(setDisb)
      .catch((e) => {
        setDisb(null);
        // 403 = the operator lacks the platform_owner/platform_admin role —
        // a polite empty state, not an error.
        if (e?.response?.status === 403) setDisbForbidden(true);
        else setDisbError(true);
      })
      .finally(() => setDisbLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <div className="p-1">
      <Space align="center" className="mb-4" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space align="center">
          <CreditCardOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>Payments</Title>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load} loading={collectionsLoading || disbLoading}>
          Refresh
        </Button>
      </Space>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <CollectionsCard data={collections} loading={collectionsLoading} error={collectionsError} />
        </Col>
        <Col xs={24} xl={12}>
          <DisbursementsCard data={disb} loading={disbLoading} error={disbError} forbidden={disbForbidden} />
        </Col>
        <Col span={24}>
          <FailedBatchesCard />
        </Col>
      </Row>
    </div>
  );
};

export default PaymentsPage;
